'use client';
import React, { useState, useEffect } from 'react';
import { Play, Eye, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { streamService, Stream } from '@/lib/services/streamService';
import StreamPlayerModal from './StreamPlayerModal';

const CARDS_PER_PAGE = 6;

const categories = [
  { id: 'cat-all', label: 'All' },
  { id: 'cat-news', label: 'News' },
  { id: 'cat-entertainment', label: 'Entertainment' },
  { id: 'cat-sports', label: 'Sports' },
  { id: 'cat-music', label: 'Music' },
  { id: 'cat-religious', label: 'Religious' },
  { id: 'cat-kids', label: 'Kids' },
  { id: 'cat-documentary', label: 'Documentary' },
];

function formatViewers(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

const countryIdToName: Record<string, string> = {
  'country-ng': 'nigeria',
  'country-gh': 'ghana',
  'country-ke': 'kenya',
  'country-za': 'south africa',
  'country-eg': 'egypt',
  'country-et': 'ethiopia',
  'country-tz': 'tanzania',
  'country-ug': 'uganda',
  'country-sn': 'senegal',
  'country-ci': 'côte',
  'country-cm': 'cameroon',
  'country-rw': 'rwanda',
  'country-zm': 'zambia',
  'country-zw': 'zimbabwe',
  'country-tz2': 'tunisia',
  'country-ma': 'morocco',
};

interface LiveChannelsGridProps {
  searchQuery?: string;
  activeCountry?: string;
}

export default function LiveChannelsGrid({ searchQuery = '', activeCountry = 'country-all' }: LiveChannelsGridProps) {
  const [activeCategory, setActiveCategory] = useState('cat-all');
  const [likedChannels, setLikedChannels] = useState<Set<string>>(new Set());
  const [channels, setChannels] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStream, setSelectedStream] = useState<Stream | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    streamService.getTVChannels().then((data) => {
      setChannels(data);
      setLoading(false);
    });
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeCountry, activeCategory]);

  const filtered = channels.filter((c) => {
    const categoryMatch =
      activeCategory === 'cat-all' ||
      c.category.toLowerCase() === activeCategory.replace('cat-', '').toLowerCase();
    const countryMatch =
      activeCountry === 'country-all' ||
      c.country.toLowerCase().includes(countryIdToName[activeCountry] ?? '');
    const q = searchQuery.toLowerCase().trim();
    const searchMatch =
      !q ||
      c.name.toLowerCase().includes(q) ||
      c.country.toLowerCase().includes(q) ||
      c.category.toLowerCase().includes(q);
    return categoryMatch && countryMatch && searchMatch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / CARDS_PER_PAGE));
  const paginated = filtered.slice((currentPage - 1) * CARDS_PER_PAGE, currentPage * CARDS_PER_PAGE);

  const toggleLike = (id: string) => {
    setLikedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div>
          <h2 className="text-base sm:text-lg font-700 text-white">Live TV</h2>
          <p className="text-xs text-[rgb(115,115,115)] mt-0.5">
            {loading ? 'Loading streams...' : `${filtered.length} stream${filtered.length !== 1 ? 's' : ''} available`}
          </p>
        </div>
      </div>

      {/* Category chips */}
      <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide mb-4 sm:mb-5 pb-1">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-600 whitespace-nowrap flex-shrink-0 transition-all duration-150 ${
              activeCategory === cat.id
                ? 'bg-[rgba(220,38,38,0.15)] text-red-500 border border-[rgba(220,38,38,0.3)]'
                : 'bg-[rgb(15,15,15)] text-[rgb(115,115,115)] border border-[rgb(38,38,38)] hover:text-white hover:border-[rgb(64,64,64)]'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={`skel-${i}`}
              className="bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl overflow-hidden animate-pulse"
            >
              <div className="aspect-video bg-[rgb(24,24,24)]" />
              <div className="p-3 space-y-2">
                <div className="h-3 bg-[rgb(24,24,24)] rounded w-3/4" />
                <div className="h-3 bg-[rgb(24,24,24)] rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Channel grid */}
      {!loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {paginated.map((channel) => (
              <div
                key={channel.id}
                onClick={() => setSelectedStream(channel)}
                className="bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl overflow-hidden card-hover group cursor-pointer"
              >
                {/* Thumbnail */}
                <div className="relative aspect-video overflow-hidden">
                  {channel.thumbnail ? (
                    <img
                      src={channel.thumbnail}
                      alt={channel.thumbnailAlt ?? channel.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-[rgb(24,24,24)] flex items-center justify-center">
                      <span className="text-3xl">{channel.flag}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[rgba(0,0,0,0.7)] to-transparent" />

                  {/* Status badge */}
                  <div className="absolute top-2.5 left-2.5">
                    {channel.streamStatus === 'live' && (
                      <span className="flex items-center gap-1 bg-red-600 text-white text-[10px] font-700 px-2 py-0.5 rounded-full">
                        <span className="w-1 h-1 rounded-full bg-white live-pulse" />
                        LIVE
                      </span>
                    )}
                    {channel.streamStatus === 'degraded' && (
                      <span className="flex items-center gap-1 bg-amber-500 text-white text-[10px] font-700 px-2 py-0.5 rounded-full">
                        ⚠ DEGRADED
                      </span>
                    )}
                    {channel.streamStatus === 'offline' && (
                      <span className="flex items-center gap-1 bg-[rgb(38,38,38)] text-[rgb(163,163,163)] text-[10px] font-700 px-2 py-0.5 rounded-full">
                        OFFLINE
                      </span>
                    )}
                  </div>

                  {/* Viewer count */}
                  {channel.streamStatus !== 'offline' && (
                    <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-[rgba(0,0,0,0.7)] text-white text-[11px] px-2 py-0.5 rounded-full">
                      <Eye size={10} />
                      <span className="tabular-nums">{formatViewers(channel.viewers)}</span>
                    </div>
                  )}

                  {/* Hover play */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedStream(channel); }}
                      className="w-10 h-10 rounded-full bg-[rgba(220,38,38,0.9)] flex items-center justify-center active:scale-95"
                    >
                      <Play size={16} fill="white" className="text-white ml-0.5" />
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex items-center gap-2">
                      {channel.logoUrl && (
                        <img
                          src={channel.logoUrl}
                          alt={`${channel.name} logo`}
                          className="w-7 h-7 rounded-full object-cover flex-shrink-0 border border-[rgb(38,38,38)] bg-[rgb(24,24,24)]"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-600 text-white truncate">{channel.name}</p>
                        <p className="text-xs text-[rgb(115,115,115)] mt-0.5">
                          {channel.flag} {channel.country}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleLike(channel.id); }}
                      className={`flex-shrink-0 p-1 rounded transition-all ${
                        likedChannels.has(channel.id)
                          ? 'text-red-400' : 'text-[rgb(64,64,64)] hover:text-red-400'
                      }`}
                    >
                      <Heart size={14} fill={likedChannels.has(channel.id) ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] font-500 text-red-500 bg-[rgba(220,38,38,0.1)] px-1.5 py-0.5 rounded">
                      {channel.category}
                    </span>
                    <span className="text-[10px] text-[rgb(115,115,115)]">{channel.language}</span>
                  </div>
                </div>
              </div>
            ))}

            {!loading && filtered.length === 0 && (
              <div className="col-span-full text-center py-12 text-[rgb(115,115,115)]">
                {searchQuery
                  ? `No streams found matching "${searchQuery}".`
                  : 'No streams found for this filter.'}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-[rgb(38,38,38)]"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={`page-${page}`}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg text-sm font-600 transition-all ${
                    page === currentPage
                      ? 'bg-red-600 text-white' :'text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] border border-[rgb(38,38,38)]'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-[rgb(38,38,38)]"
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {selectedStream && (
        <StreamPlayerModal
          stream={selectedStream}
          onClose={() => setSelectedStream(null)}
        />
      )}
    </section>
  );
}
